/**
 * Os quinze papeis do TEG, para tipar rota protegida e condicao de tela.
 *
 * A MESMA lista existe no realm do Keycloak, no tipo enumerado `app_role` do
 * banco e em apps/api/src/nucleo/papeis.ts. Aqui ela nao decide nada de
 * seguranca: quem diz quais papeis a pessoa tem e o GET /eu, e a API e o banco
 * cobram de novo. Isto e vocabulario, para o TypeScript recusar
 * `papeis={['ADMINISTRADOR']}` antes de virar rota que ninguem acessa.
 *
 * Referencia: docs/AUTENTICACAO-E-ACESSO.md, secao 5.
 */
export const PAPEIS = [
  // Internos DTP
  'ADMIN',
  'GESTOR_ADMIN',
  'GESTOR',
  'OPERADOR',
  'FINANCEIRO',
  'FISCAL',
  'AUDITOR',
  // Rede de ensino
  'GESTOR_SME',
  'OPERADOR_SME',
  'GESTOR_DRE',
  'OPERADOR_DRE',
  // Externos — empresa credenciada
  'ADMIN_CREDENCIADO',
  'FINANCEIRO_CREDENCIADO',
  'OPERADOR_CREDENCIADO',
  'CREDENCIADO',
] as const;

export type Papel = (typeof PAPEIS)[number];

/** Papeis da empresa credenciada: entram pelo portal, nao pela operacao. */
export const PAPEIS_EXTERNOS = [
  'ADMIN_CREDENCIADO',
  'FINANCEIRO_CREDENCIADO',
  'OPERADOR_CREDENCIADO',
  'CREDENCIADO',
] as const satisfies readonly Papel[];

const CONJUNTO_EXTERNOS = new Set<string>(PAPEIS_EXTERNOS);

/**
 * Papel novo nasce interno, como na API: errar para o lado de exigir mais
 * (apps/api/src/nucleo/papeis.ts).
 */
export function ehInterno(papel: string): boolean {
  return !CONJUNTO_EXTERNOS.has(papel);
}

/**
 * Como cada papel se chama na tela.
 *
 * Os nomes saem das descricoes dos papeis no realm
 * (infra/keycloak/realm-teg.json), que e onde eles foram escritos e aprovados —
 * e nao de `humanizeLabel`, que transformaria GESTOR_ADMIN em "Gestor Admin".
 * Encurtados onde a descricao do realm carrega explicacao de escopo, que cabe na
 * documentacao e nao num selo de perfil.
 */
export const ROTULO_DO_PAPEL: Record<Papel, string> = {
  ADMIN: 'Administrador do sistema',
  GESTOR_ADMIN: 'Gestor administrativo',
  GESTOR: 'Gestor',
  OPERADOR: 'Operador',
  FINANCEIRO: 'Financeiro',
  FISCAL: 'Fiscalização',
  AUDITOR: 'Auditoria',
  GESTOR_SME: 'Gestor SME',
  OPERADOR_SME: 'Operador SME',
  GESTOR_DRE: 'Gestor de diretoria regional',
  OPERADOR_DRE: 'Operador de diretoria regional',
  ADMIN_CREDENCIADO: 'Administrador da empresa credenciada',
  FINANCEIRO_CREDENCIADO: 'Financeiro da empresa credenciada',
  OPERADOR_CREDENCIADO: 'Operador da empresa credenciada',
  CREDENCIADO: 'Transportador credenciado',
};

/** Rotulo do papel, com o codigo cru como ultimo recurso. */
export function rotuloDoPapel(papel: string): string {
  return ROTULO_DO_PAPEL[papel as Papel] ?? papel;
}
